import axios from 'axios';

// Use relative URL for proxy setup, or full URL from env for production
const API_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'http://localhost:8001')
  : ''; // Empty string means relative URLs through proxy

// Create axios instance with default config.
//
// IMPORTANT: a default ``timeout`` is set to prevent UI components from
// hanging indefinitely when the backend (or Cloudflare in front of it)
// stalls. Without this, axios waits forever and any "Loading..." spinner
// stays on screen until the user reloads the tab. The 30 s value is
// generous enough to cover slow LLM-backed endpoints that the UI awaits
// directly (LLM calls behind ``apply_async`` are not affected because
// they run on Celery and only the ``poll`` endpoints come back here).
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors with token refresh mechanism
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      // Check if we have a refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        // No refresh token, clear everything and reject
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        return Promise.reject(error);
      }

      // Mark that we're attempting a refresh
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post(
          `${API_URL}/api/v1/auth/refresh`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${refreshToken}`
            }
          }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Store new tokens
        localStorage.setItem('token', access_token);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Process queued requests
        processQueue(null, access_token);

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and reject
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        
        // Don't redirect here - let the app handle navigation
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors or if refresh already attempted, just reject
    return Promise.reject(error);
  }
);

// Registration and verification API functions
export const registerUser = async (userData) => {
  return apiClient.post('/api/v1/auth/register', userData);
};

export const checkEmailAvailability = async (email) => {
  return apiClient.post('/api/v1/auth/check-email', { email });
};

export const checkPhoneAvailability = async (phoneNumber) => {
  return apiClient.post('/api/v1/auth/check-phone', { phone_number: phoneNumber });
};

export const verifyEmail = async (email, code) => {
  return apiClient.post('/api/v1/auth/verify-email', { email, code });
};

export const resendVerificationCode = async (email) => {
  return apiClient.post('/api/v1/auth/resend-verification-code', { email });
};

export const getVerificationStatus = async (email) => {
  return apiClient.get(`/api/v1/auth/verification-status/${encodeURIComponent(email)}`);
};

export const logoutUser = async () => {
  return apiClient.post('/api/v1/auth/logout');
};

// Device management API functions
export const getLoginHistory = async (limit = 20, offset = 0) => {
  return apiClient.get(`/api/v1/auth/login-history?limit=${limit}&offset=${offset}`);
};

export const deleteSession = async (sessionId) => {
  return apiClient.delete(`/api/v1/auth/sessions/${sessionId}`);
};

export const logoutAllOtherDevices = async () => {
  return apiClient.post('/api/v1/auth/sessions/logout-all-others');
};

// Account deletion API functions
export const requestAccountDeletion = async (reason) => {
  return apiClient.post('/api/v1/auth/request-deletion', { reason });
};

export const cancelAccountDeletion = async () => {
  return apiClient.post('/api/v1/auth/cancel-deletion');
};

export const getAccountStatus = async () => {
  return apiClient.get('/api/v1/auth/account-status');
};

// Data export API functions
export const exportUserData = async () => {
  let response;
  try {
    response = await apiClient.get('/api/v1/auth/export-data', {
      responseType: 'blob',
    });
  } catch (err) {
    // When ``responseType: 'blob'`` is set, axios returns the error body
    // as a Blob — decode it so the caller can show the real backend
    // detail (auth, server error) instead of an opaque generic message.
    const blob = err.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        let detail = text;
        try {
          const parsed = JSON.parse(text);
          detail = parsed.detail || parsed.message || text;
        } catch (_) { /* not JSON */ }
        const status = err.response?.status;
        throw new Error(
          `Data export failed${status ? ` (HTTP ${status})` : ''}: ${detail}`
        );
      } catch (decodeErr) {
        if (decodeErr instanceof Error && decodeErr.message.startsWith('Data export failed')) throw decodeErr;
      }
    }
    throw err;
  }

  // Create a blob URL and trigger download
  const blob = new Blob([response.data], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  // Get filename from Content-Disposition header
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'smartgain_data_export.json';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

// GDPR audit API functions
export const getGDPRAudit = async (limit = 100, offset = 0) => {
  return apiClient.get(`/api/v1/auth/gdpr-audit?limit=${limit}&offset=${offset}`);
};

export default apiClient;
